<template>
    <div class="form-control w-full">
        <label
            class="label"
            :for="name">
            <span class="label-text">
                {{ label }}<span
                    v-if="required"
                    class="text-error-500 ml-1">
                    *
                </span>
            </span>
        </label>
        <input
            :id="name"
            ref="fileInput"
            type="file"
            class="file-input file-input-bordered w-full"
            :name="prop"
            :class="cColorSize"
            :accept="accept"
            :multiple="multiple"
            :disabled="disabled"
            :required="required"
            @change="onFileChange" >
        <span
            v-for="(n, index) in cFileName"
            :key="index"
            class="label">
            {{ n }}
        </span>
        <spans class="label">
            <span
                v-if="errorMessage"
                class="label-text-alt text-rose-500">
                {{ errorMessage }}
            </span>
            <span
                v-if="description"
                class="label-text-alt">
                >{{ description }}
            </span>
        </spans>
    </div>
</template>

<script lang="ts" setup>
import { useField, useFieldError } from 'vee-validate'
import { daisyuiColorSize } from '~/components/utils/daisyui'
const props = defineProps({
  modelValue: {
    type: [Array<File>, File, String],
    required: true
  },
  label: String,
  placeholder: String,
  description: String,
  prop: String,
  name: String,
  rules: Object,
  size: String,
  color: String,
  accept: String,
  multiple: Boolean,
  required: Boolean,
  disabled: Boolean,
  readonly: Boolean,
  fileName: {
    type: [Array<String>, String, null, undefined]
  }
})

const { modelValue, prop, name, rules, multiple, disabled, required, readonly, fileName } = toRefs(props)
const emits = defineEmits(['update:modelValue'])
const cColorSize = daisyuiColorSize('input', props)
const fileInput = templateRef<HTMLInputElement | null>('fileInput', null)
let field: any
let errorMessage: undefined | ComputedRef
const cProp = computed(() => prop?.value)

const setFields = (prop: any) => {
  if (typeof prop === 'string' && prop !== '') {
    field = useField(prop, undefined, {
      label: name?.value || prop,
      validateOnValueUpdate: true,
      validateOnMount: false
    })
    errorMessage = useFieldError(cProp.value)
  }
}

watch(
  () => cProp.value,
  (val: any) => {
    setFields(val)
  }
)

onMounted(() => {
  setFields(cProp.value)
})

const cValue = computed({
  get () {
    return modelValue.value
  },
  set (val) {
    field?.setValue(val)
    emits('update:modelValue', val)
  }
})

const cFileName = computed(() => {
  if (cValue.value && Array.isArray(cValue.value) && cValue.value.length) {
    return []
  }

  if (!fileName?.value) {
    return []
  }

  if (Array.isArray(fileName.value)) {
    return fileName.value
  }

  return [fileName.value]
})

watch(
  () => cValue.value,
  (val: any) => {
    field?.setValue(val)
  }
)

watch(
  () => modelValue.value,
  (val: File | File[] | string) => {
    if (fileInput.value) {
      if (val === undefined || val === null || val === '') {
        fileInput.value.value = ''
      }
    }
  }
)
const onFileChange = (e: any) => {
  const target = e.target
  if (target && target.files) {
    if (multiple.value) {
      cValue.value = target.files
    } else {
      cValue.value = target.files[0]
    }
  }
}

onMounted(() => {
  if (modelValue !== undefined && modelValue.value !== undefined) {
    field?.setValue(modelValue.value)
  }
})
</script>
